import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useStore } from '@hub/helpers/use-store';
import { useColor } from '@hub/hooks';
import {
  Card,
  Col,
  Flex,
  ProgressSlider,
  Row,
  TextComponent,
} from '@monorepo/react-components';
import { useResponsiveness } from '@monorepo/react-hooks';
import { IconsNames } from '@monorepo/types';
import { ConsistencyRules, ThemeColors } from '@monorepo/types';
import { AccountRulesCardIcon } from '../account-rules-icon/account-rules-icon';

import '../account-rules.scss';

interface Props {
  programId: string;
  isLoading: boolean;
  currentTradesNumber?: number;
}

const MinimumTradesRuleCard: FunctionComponent<Props> = ({
  programId,
  isLoading,
  currentTradesNumber = 0,
}) => {
  const { getColor } = useColor();
  const { isMobile } = useResponsiveness();
  const { t } = useTranslation(['dashboardTemplates']);

  const {
    dataStore: { programStore },
  } = useStore();

  const minimumTradesRule = programStore.getRule(
    programId,
    ConsistencyRules.MinimumTrades,
  );

  const requiredTradesNumber = minimumTradesRule?.value;

  if (!requiredTradesNumber) {
    return null;
  }

  const currentPercentage = (currentTradesNumber / requiredTradesNumber) * 100;

  const completeInPercentage =
    currentPercentage > 100 ? 100 : currentPercentage;

  const completedTradesNumber =
    currentTradesNumber > requiredTradesNumber
      ? requiredTradesNumber
      : currentTradesNumber;

  return (
    <Card hoverable={false} className={'account-rules'}>
      <Row
        gutter={[5, 10]}
        className={classNames({
          'account-rules__row--mobile': isMobile,
        })}
      >
        <Col
          span={8}
          sm={24}
          className={classNames({
            'account-rules__col--mobile': isMobile,
          })}
        >
          <Flex
            justify={'space-between'}
            gap={5}
            className={classNames('account-rules__header', {
              'account-rules__mobile': isMobile,
            })}
          >
            <Col span={18}>
              <Flex gap={5} align={'center'} wrap="wrap">
                <TextComponent
                  responsive={false}
                  color={getColor(ThemeColors.colorPrimary)}
                  weight={600}
                >
                  {t('dashboardTemplates:minimum_trades')}
                </TextComponent>
              </Flex>
            </Col>
            <Row>
              <AccountRulesCardIcon iconName={IconsNames.Room} />
            </Row>
          </Flex>
        </Col>

        <Col span={16} sm={24}>
          <Flex
            justify={'end'}
            vertical
            className={'account-rules__slider-section'}
          >
            <Flex justify="space-between" align="end">
              <Flex align="center">
                <TextComponent
                  loading={isLoading}
                  weight={600}
                  level={3}
                  responsive={false}
                >
                  {completedTradesNumber}/
                </TextComponent>
                <TextComponent level={3} responsive={false}>
                  {requiredTradesNumber}
                </TextComponent>
              </Flex>
              <TextComponent loading={isLoading} level={4} responsive={false}>
                {completeInPercentage?.toFixed(0)}%
              </TextComponent>
            </Flex>

            <ProgressSlider
              disabled
              value={currentTradesNumber}
              trackColor={ThemeColors.colorSuccess}
              max={requiredTradesNumber}
            />
            <TextComponent level={8} responsive={false}>
              {t('dashboardTemplates:should_have_minimum_trades', {
                value: requiredTradesNumber,
              })}
            </TextComponent>
          </Flex>
        </Col>
      </Row>
    </Card>
  );
};

export default MinimumTradesRuleCard;
